import { useState, lazy, Suspense } from "react";
import { Sparkles, Shield, Zap, Eye, BarChart3, Lock, Loader2 } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { 
  UserData, 
  PhotoData, 
  AnalysisResult, 
  FacialLandmarks, 
  ProfileLandmarks,
  FeatureCharacteristics 
} from "@/types/analysis";
import { DEFAULT_FEATURE_CHARACTERISTICS } from "@/types/analysis";

// Lazy load heavy components that aren't needed on initial render
const PhotoUploader = lazy(() => import("@/components/upload/PhotoUploader").then(m => ({ default: m.PhotoUploader })));
const UserDataForm = lazy(() => import("@/components/upload/UserDataForm").then(m => ({ default: m.UserDataForm })));
const FeatureCharacteristicsForm = lazy(() => import("@/components/upload/FeatureCharacteristicsForm").then(m => ({ default: m.FeatureCharacteristicsForm })));
const PhotoInstructions = lazy(() => import("@/components/upload/PhotoInstructions").then(m => ({ default: m.PhotoInstructions })));
const LandmarkAnnotator = lazy(() => import("@/components/annotate/LandmarkAnnotator").then(m => ({ default: m.LandmarkAnnotator })));
const ResultsDashboard = lazy(() => import("@/components/results/ResultsDashboard").then(m => ({ default: m.ResultsDashboard })));

// Lazy load analysis engine (heavy computation)
const runAnalysisModule = () => import("@/lib/analysisEngine");

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const defaultUserData: UserData = {
  sex: "male",
  age: 25,
  ethnicity: "caucasian",
  skinColor: "medium",
  heightCm: 175,
  weightKg: 70,
};

const features = [
  { icon: Eye, title: "Mark Your Features", description: "Precisely mark facial landmarks for accurate analysis" },
  { icon: BarChart3, title: "30+ Ratios Analyzed", description: "FWHR, canthal tilt, gonial angle & more" },
  { icon: Lock, title: "100% Private", description: "All processing happens in your browser" },
  { icon: Zap, title: "Feature Ratings", description: "Get rated on each individual feature" },
];

export default function Index() {
  const [step, setStep] = useState<"landing" | "upload" | "annotate" | "results">("landing");
  const [photos, setPhotos] = useState<PhotoData>({ frontPhoto: null, sidePhoto: null });
  const [userData, setUserData] = useState<UserData>(defaultUserData);
  const [characteristics, setCharacteristics] = useState<FeatureCharacteristics>(DEFAULT_FEATURE_CHARACTERISTICS);
  const [result, setResult] = useState<AnalysisResult | null>(null);

  const canProceedToAnnotate = photos.frontPhoto !== null;

  const handleAnnotationComplete = async (facialLandmarks: FacialLandmarks, profileLandmarks: ProfileLandmarks | null) => {
    const { runAnalysis } = await runAnalysisModule();
    const analysisResult = runAnalysis(
      userData, 
      photos, 
      false, 
      facialLandmarks, 
      profileLandmarks, 
      characteristics
    );
    setResult(analysisResult);
    setStep("results");
  };

  const handleBack = () => {
    if (step === "results") {
      setStep("annotate");
      setResult(null);
    } else if (step === "annotate") {
      setStep("upload");
    } else {
      setStep("landing");
    }
  };

  if (step === "annotate" && photos.frontPhoto) {
    return (
      <>
        <Header showQuickScan={false} />
        <main className="pt-16">
          <Suspense fallback={<LoadingFallback />}>
            <LandmarkAnnotator
              frontPhoto={photos.frontPhoto}
              sidePhoto={photos.sidePhoto}
              onComplete={handleAnnotationComplete}
              onBack={() => setStep("upload")}
            />
          </Suspense>
        </main>
      </>
    );
  }

  if (step === "results" && result) {
    return (
      <>
        <Header showQuickScan={false} />
        <main className="pt-16">
          <Suspense fallback={<LoadingFallback />}>
            <ResultsDashboard result={result} onBack={handleBack} />
          </Suspense>
        </main>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header onQuickScan={() => setStep("upload")} showQuickScan={step === "landing"} />
      <main>

      {step === "landing" ? (
        <>
          {/* Hero Section */}
          <section className="pt-24 pb-16 px-4 relative overflow-hidden">
            <div className="absolute inset-0 grid-pattern opacity-30" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl" />
            
            <div className="container mx-auto text-center relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                <span>100% Private • Client-Side Processing</span>
              </div>

              <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold mb-6 leading-tight">
                <span className="gradient-text">Precision</span>
                <br />
                Facial Analysis
              </h1>

              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                Mark your facial features for accurate attractiveness scores, PSL ratings, and detailed ratio analysis. 
                All processing happens locally in your browser.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="hero" size="xl" onClick={() => setStep("upload")}>
                  <Sparkles className="w-5 h-5" />
                  Start Analysis
                </Button>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-16 px-4">
            <div className="container mx-auto">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {features.map((feature, index) => (
                  <Card key={index} variant="interactive" className="text-center p-6">
                    <CardContent className="p-0 space-y-3">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto">
                        <feature.icon className="w-6 h-6 text-primary" />
                      </div>
                      <h2 className="font-display font-semibold text-base">{feature.title}</h2>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        </>
      ) : (
        /* Upload Step */
        <section className="pt-24 pb-16 px-4">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-display font-bold mb-2">Upload Your Photos</h1>
              <p className="text-muted-foreground">
                Upload front and side profile photos, then mark your facial features
              </p>
            </div>

            <Suspense fallback={<LoadingFallback />}>
              <PhotoInstructions />

              <div className="grid lg:grid-cols-4 gap-6">
                <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
                  <PhotoUploader
                    type="front"
                    photo={photos.frontPhoto}
                    onPhotoChange={(photo) => setPhotos((p) => ({ ...p, frontPhoto: photo }))}
                  />
                  <PhotoUploader
                    type="side"
                    photo={photos.sidePhoto}
                    onPhotoChange={(photo) => setPhotos((p) => ({ ...p, sidePhoto: photo }))}
                  />
                </div>
                <UserDataForm userData={userData} onUserDataChange={setUserData} />
                <FeatureCharacteristicsForm 
                  characteristics={characteristics} 
                  onCharacteristicsChange={setCharacteristics} 
                />
              </div>
            </Suspense>

            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="ghost" onClick={() => setStep("landing")}>
                Back
              </Button>
              <Button
                variant="hero"
                size="lg"
                onClick={() => setStep("annotate")}
                disabled={!canProceedToAnnotate}
              >
                <Sparkles className="w-5 h-5" />
                Mark Features
              </Button>
            </div>
          </div>
        </section>
      )}
      </main>
    </div>
  );
}
